import React, { Dispatch, SetStateAction } from "react";
import { copyTextToClipboard } from "@/utils/helpers";
import { ClipboardIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { FramerButton } from "@/components/motion";
import { Girocode } from "react-girocode";
import myronLogo from "@/assets/MYRON-icon-transparent.webp";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/opacity.css";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useMyReferenceNumber } from "@/hooks/queries";

type Props = {
  amount: number;
  setAmount: Dispatch<SetStateAction<number>>;
}

function DepositBankDetailsComponent({ amount, setAmount }: Props) {
  // Hooks
  const { t } = useTranslation(["modals"]);
  const { data: referenceData, isPending: isReferencePending } = useMyReferenceNumber();

  // Bank Account details
  const paymentDetails = {
    iban: import.meta.env.VITE_MYRON_IBAN,
    bic: import.meta.env.VITE_MYRON_BIC,
    recipient: import.meta.env.VITE_MYRON_RECIPIENT,
    reference: referenceData?.referenceNumber ?? "",
  };

  const bankDetails = [
    {
      title: t("depositsummary.bankdetails.accountholder"),
      value: paymentDetails.recipient
    }
  ];

  const accountDetails = [
    {
      id: "iban",
      title: "IBAN",
      value: paymentDetails.iban,
    },
    {
      id: "bic",
      title: "BIC",
      value: paymentDetails.bic,
    },
    {
      id: "reference",
      title: t("depositsummary.bankdetails.reference"),
      value: paymentDetails.reference,
      isPending: isReferencePending,
    },
  ];

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const val = parseFloat(event.target.value);
    setAmount(isNaN(val) ? 0 : val);
  }

  return (
    <div>
      {/* Subtext */}
      <div className="mb-4 dark:text-gray-100">
        <p>
          {t("depositsummary.depositnote")}
        </p>
      </div>

      {/* Bank details */}
      <div>
        <p className="text-xl font-semibold mb-2 dark:text-gray-100">
          {t("depositsummary.bankdetails.title")}
        </p>
        <div className="flex flex-wrap">
          <div className="flex-1 rounded bg-gray-100 dark:bg-mfdarklighter p-4">
            {/* Bank details */}
            {
              bankDetails.map((item) => (
                <div key={`bankdetails:${item.title}`}>
                  <p className="text-sm text-gray-400 pb-1">{item.title}</p>
                  <p className="dark:text-gray-100 select-text">{item.value}</p>
                </div>
              ))
            }
            {/* Account details */}
            {
              accountDetails.map((item) => (
                <div
                  key={`accountdetails:${item.id}`}
                  className="flex items-end justify-between"
                >
                  {/* Details */}
                  <div>
                    <p className="text-sm text-gray-400 pb-1">{item.title}</p>
                    <p className="text-lg font-semibold dark:text-gray-100 select-text break-all sm:break-normal">
                      {item.isPending ? "…" : item.value}
                    </p>
                  </div>
                  {/* Copy to clipboard */}
                  <div>
                    <FramerButton
                      variant="ghost"
                      size="icon"
                      className="border-mfpurple bg-transparent dark:border-mforange hover:bg-gray-300 dark:hover:bg-gray-800"
                      disabled={!item.value}
                      onClick={() => copyTextToClipboard(item.value)}
                    >
                      <ClipboardIcon
                        className="w-6 h-6 cursor-pointer dark:text-gray-100"
                      />
                    </FramerButton>
                  </div>
                </div>
              ))
            }
          </div>

          {/* QR Code container */}
          <div className="flex flex-1 flex-col p-4 gap-y-2">
            {/* Amount input */}
            <div>
              <Label htmlFor="deposit-amount">
                {t("depositsummary.amountinput.label")}
              </Label>
              <Input
                id="deposit-amount"
                type="number"
                name="amount"
                value={amount || ""}
                onChange={handleChange}
                className="min-w-[150px] mt-2"
                placeholder="0"
                autoFocus
              />
            </div>
            {/* QR Code */}
            <div className="flex justify-center mt-4">
              {
                !amount || amount === 0 ?
                  <div className="flex items-center justify-center h-[200px] w-[200px] border border-gray-200 dark:border-mfdarklighter">
                    <LazyLoadImage
                      alt={"Myron's logo"}
                      className="w-[80px]"
                      src={myronLogo}
                      effect="opacity"
                    />
                  </div>
                  :
                  <div className="[&>svg]:w-[200px] [&>svg]:h-[200px]">
                    <Girocode
                      recipient={paymentDetails.recipient}
                      iban={paymentDetails.iban}
                      amount={amount}
                      bic={paymentDetails.bic}
                      {...(paymentDetails.reference ? { reference: paymentDetails.reference } : {})}
                    />
                  </div>
              }
            </div>
          </div>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="my-4 dark:text-gray-100">
        <p>
          {t("depositsummary.disclaimer")}
        </p>
      </div>
    </div>
  );
}

export default DepositBankDetailsComponent;
